import React, { useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { CheckedState } from '@radix-ui/react-checkbox'

import {
  TwitterAuthExpired,
  TwitterAuthOccupied,
  TwitterAuthSuccess
} from '@/assets/images/auth'
import {
  TwitterAuthState,
  useTwitterAuth
} from '@/hooks/services/useTwitterAuth'
import { twitterAuthStateAtom } from '@/stores/services'

import { Flex } from './common/Box'
import { AsyncButton } from './common/Button'
import Checkbox from './common/Checkbox'
import { ProDialog } from './common/Dialog'
import { FileRocket } from './common/Lottie'
import { Text } from './common/Text'

const TwitterAuth = React.forwardRef<React.ComponentRef<typeof ProDialog>>(
  (_, ref) => {
    const { t } = useTranslation()
    const [agree, setAgree] = useState<CheckedState>(false)
    const [open, setOpen] = useState(false)

    const twitterAuthState = useAtomValue(twitterAuthStateAtom)

    const { getAuthLink, checkLoading } = useTwitterAuth()

    const titleMap = {
      [TwitterAuthState.UN_KNOWN]: t('twitter.auth.dialog.title'),
      [TwitterAuthState.UNAUTHENTICATED]: t('twitter.auth.dialog.title'),
      [TwitterAuthState.AUTHENTICATED]: t('twitter.auth.success'),
      [TwitterAuthState.REDIRECTING]: t('twitter.auth.pending'),
      [TwitterAuthState.EXPIRED]: t('twitter.auth.expired'),
      [TwitterAuthState.OCCUPIED]: t('twitter.auth.occupied')
    }

    useImperativeHandle(
      ref,
      () => ({
        open: () => setOpen(true),
        close: () => setOpen(false)
      }),
      []
    )

    return (
      <ProDialog
        open={open}
        title={titleMap[twitterAuthState]}
        contentProps={{
          className: 'xl:max-w-none xl:w-[544px] gap-2 xl:gap-6'
        }}
        onOpenChange={setOpen}
      >
        {twitterAuthState === TwitterAuthState.UNAUTHENTICATED && (
          <>
            <div>
              <Text>{t('twitter.auth.dialog.description.p1')}</Text>
              <Text>{t('twitter.auth.dialog.description.p2')}</Text>
              {[
                t('twitter.auth.dialog.description.l1'),
                t('twitter.auth.dialog.description.l2'),
                t('twitter.auth.dialog.description.l3')
              ].map((text, index) => (
                <Text key={index} className="pl-2">
                  · {text}
                </Text>
              ))}
              <Text>{t('twitter.auth.dialog.description.end')}</Text>
              <Flex className="mt-4 items-center gap-2 xl:mt-8">
                <Checkbox
                  id="twitter-auth"
                  checked={agree}
                  onCheckedChange={(checked) => setAgree(checked)}
                />
                <label htmlFor="twitter-auth" className="text-xs xl:text-base">
                  {t('twitter.auth.dialog.agree')}
                </label>
              </Flex>
            </div>
            <AsyncButton
              connection
              className="!flex font-Orbitron"
              disabled={!agree}
              onClick={getAuthLink}
            >
              {t('twitter.auth.connect')}
            </AsyncButton>
          </>
        )}
        {twitterAuthState === TwitterAuthState.REDIRECTING && (
          <>
            <div className="mx-auto py-4">
              <FileRocket />
            </div>
            <Text>{t('twitter.auth.pending.description')}</Text>
            <AsyncButton
              className="!flex font-Orbitron"
              disabled={checkLoading}
              loading={checkLoading}
              onClick={() => setOpen(false)}
            >
              {t('common.confirm')}
            </AsyncButton>
          </>
        )}
        {twitterAuthState === TwitterAuthState.AUTHENTICATED && (
          <>
            <img
              src={TwitterAuthSuccess}
              alt="Twitter Auth Success"
              className="mx-auto h-[136px]"
            />
            <Text>{t('twitter.auth.success.description')}</Text>
            <AsyncButton
              className="!flex font-Orbitron"
              onClick={() => setOpen(false)}
            >
              {t('common.confirm')}
            </AsyncButton>
          </>
        )}
        {twitterAuthState === TwitterAuthState.EXPIRED && (
          <>
            <img
              src={TwitterAuthExpired}
              alt="Twitter Auth Expired"
              className="mx-auto h-[136px]"
            />
            <Text>{t('twitter.auth.expired.description')}</Text>
            <AsyncButton
              connection
              className="!flex font-Orbitron"
              onClick={getAuthLink}
            >
              {t('twitter.auth.reconnect')}
            </AsyncButton>
          </>
        )}
        {twitterAuthState === TwitterAuthState.OCCUPIED && (
          <>
            <img
              src={TwitterAuthOccupied}
              alt="Twitter Auth Occupied"
              className="mx-auto h-[136px]"
            />
            <Text>{t('twitter.auth.occupied.description')}</Text>
            <AsyncButton
              className="!flex font-Orbitron"
              onClick={() => setOpen(false)}
            >
              {t('common.confirm')}
            </AsyncButton>
          </>
        )}
      </ProDialog>
    )
  }
)

export default TwitterAuth
